// =============================================================================
// Portal "My Products" Page — cross-store listing of published products
// -----------------------------------------------------------------------------
// Read-only view: shows every product the merchant has published to any of
// their connected stores (Shopify today). Publish/edit/unpublish flows live
// in the Shopify embedded app where the merchant already has store context.
//
// Data source: GET /api/v1/products (org-scoped server-side via
// Connection.organizationId — see apps/api/src/modules/product).
// =============================================================================

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Card,
  Input,
  Select,
  Badge,
  Button,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { StoreFilter } from '../components/StoreFilter';

const PRODUCT_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'SYNC_ERROR', label: 'Sync Error' },
];

const SYNC_STATUSES = [
  { value: '', label: 'All Sync States' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SYNCING', label: 'Syncing' },
  { value: 'SYNCED', label: 'Synced' },
  { value: 'ERROR', label: 'Error' },
  { value: 'SYNC_REQUIRED', label: 'Sync required' },
];

const SORT_OPTIONS = [
  { value: 'desc', label: 'Newest First' },
  { value: 'asc', label: 'Oldest First' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'design', label: 'Designed by me' },
  { value: 'template', label: 'Published to store' },
];

const STATUS_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'default',
  SYNC_ERROR: 'danger',
};

const SYNC_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  PENDING: 'default',
  SYNCING: 'info',
  SYNCED: 'success',
  ERROR: 'danger',
  SYNC_REQUIRED: 'warning',
  // Legacy status (pre full-sync model); auto-resolves on next sync.
  DRIFT_DETECTED: 'warning',
};

/** Human labels for sync badges (raw enum words are unclear for merchants). */
const SYNC_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  SYNCING: 'Syncing',
  SYNCED: 'Synced',
  ERROR: 'Error',
  SYNC_REQUIRED: 'Sync required',
  DRIFT_DETECTED: 'Sync required',
};

interface RenderSummary {
  total: number;
  pending: number;
  running: number;
  done: number;
  failed: number;
}

interface Product {
  id: string;
  title: string;
  status: string;
  syncStatus: string;
  /**
   * Where this product came from. DESIGN_GENERATED rows are
   * merchant-authored apparel designs (created via the designer's
   * "Create Product" flow); TEMPLATE_PUBLISH rows are admin-curated
   * catalog products published to a connected store.
   */
  sourceType: 'TEMPLATE_PUBLISH' | 'DESIGN_GENERATED' | string;
  /**
   * Product-level composite preview URL — set by the variant-preview
   * worker on first-finishing variant. Falls back to UPT thumbnail.
   */
  previewImageUrl: string | null;
  externalProductId: string | null;
  requiresRepublish: boolean;
  variantCount: number;
  /**
   * Aggregate variant render status (apparel multi-side v2). Powers
   * the "Rendering N / M" chip on DESIGN_GENERATED cards without
   * polling per variant.
   */
  renderSummary: RenderSummary;
  upt: {
    name: string | null;
    slug: string | null;
    thumbnailUrl: string | null;
  };
  /**
   * Nullable: DESIGN_GENERATED rows have no connection until the
   * merchant publishes to Shopify.
   */
  connection: {
    id: string;
    name: string;
    type: string;
    externalDomain: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface ProductsResponse {
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Which UPT design surface this page renders:
 *   - "my"     → My Products (designContext=PRODUCT). POD / design-generated +
 *                product-context catalog publishes.
 *   - "custom" → Custom Products (designContext=STOREFRONT). Buyer-personalized
 *                storefront products the customer designs via "Design Now".
 */
type ProductSurface = 'my' | 'custom';

interface ProductsPageProps {
  surface?: ProductSurface;
}

const SURFACE_COPY: Record<
  ProductSurface,
  { designContext: 'PRODUCT' | 'STOREFRONT'; heading: string; empty: string }
> = {
  my: {
    designContext: 'PRODUCT',
    heading: 'My Products',
    empty:
      'Open the designer to create your first apparel product, or use the Transfer Kingdom app in your Shopify admin to publish a catalog product.',
  },
  custom: {
    designContext: 'STOREFRONT',
    heading: 'Custom Products',
    empty:
      'Publish a storefront product from the Transfer Kingdom app in your Shopify admin. Customers personalize it with the built-in designer.',
  },
};

export function ProductsPage({ surface = 'my' }: ProductsPageProps = {}) {
  const surfaceCopy = SURFACE_COPY[surface];
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const syncStatus = searchParams.get('syncStatus') ?? '';
  const source = searchParams.get('source') ?? '';
  const search = searchParams.get('search') ?? '';
  const sortOrder = searchParams.get('sortOrder') ?? 'desc';
  const connectionId = searchParams.get('connectionId') ?? '';

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (status) c++;
    if (syncStatus) c++;
    if (source) c++;
    if (connectionId) c++;
    return c;
  }, [status, syncStatus, source, connectionId]);

  const [searchInput, setSearchInput] = useState(search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterRef.current('search', searchInput);
      searchDebounceRef.current = null;
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const productsQuery = useQuery({
    queryKey: [
      'portal',
      'products',
      surface,
      { page, status, syncStatus, source, search, sortOrder, connectionId },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      params.set('designContext', surfaceCopy.designContext);
      if (status) params.set('status', status);
      if (syncStatus) params.set('syncStatus', syncStatus);
      if (source) params.set('source', source);
      if (search) params.set('search', search);
      params.set('sortOrder', sortOrder);
      if (connectionId) params.set('connectionId', connectionId);
      return api.get<ProductsResponse>(`/api/v1/products?${params.toString()}`);
    },
    // Adaptive refetch: 5 s while any row is mid-render, 60 s
    // otherwise. The dedicated render-status endpoint (single
    // product) is the high-frequency channel on detail pages; the
    // listing only needs enough cadence to flip a "Rendering …" chip
    // to "Done" without a manual refresh.
    refetchInterval: (q) => {
      const rows = (q.state.data as ProductsResponse | undefined)?.data ?? [];
      const anyInFlight = rows.some(
        (r) => r.renderSummary.pending + r.renderSummary.running > 0,
      );
      return anyInFlight ? 5_000 : 60_000;
    },
  });

  const applySearchNow = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setFilter('search', searchInput);
  }, [searchInput, setFilter]);

  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch<{ id: string; status: string }>(
        `/api/v1/products/${id}/${archived ? 'archive' : 'unarchive'}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['portal', 'products'],
      });
    },
  });

  const handleArchiveToggle = useCallback(
    async (product: Product) => {
      const isArchived = product.status === 'ARCHIVED';
      if (!isArchived) {
        const ok = await confirm({
          title: 'Archive product',
          description:
            'This hides the product from your default list. It will not affect any product already published to a connected store. You can restore it anytime from the Archived filter.',
          confirmLabel: 'Archive',
          tone: 'danger',
        });
        if (!ok) return;
      }
      archiveMutation.mutate({ id: product.id, archived: !isArchived });
    },
    [confirm, archiveMutation],
  );

  const products = productsQuery.data?.data ?? [];
  const pagination = productsQuery.data?.pagination;

  return (
    <div className="space-y-6">
      {dialog}
      <Card padding="none">
        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {surfaceCopy.heading}
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              {surface === 'custom'
                ? pagination != null
                  ? `${pagination.total} custom product${pagination.total !== 1 ? 's' : ''} — your customers personalize these on the storefront`
                  : 'Storefront products your customers personalize with the built-in designer'
                : pagination != null
                  ? `${pagination.total} product${pagination.total !== 1 ? 's' : ''} — designed by you and published to your stores`
                  : 'Apparel designs you have created and catalog products published to your connected stores'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1 sm:max-w-xs">
              <Input
                placeholder="Search by title or external ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearchNow()}
                aria-label="Search products"
              />
            </div>
            <div className="w-[170px]">
              <Select
                value={source}
                onChange={(e) => setFilter('source', e.target.value)}
                options={SOURCE_OPTIONS}
                aria-label="Filter by source"
              />
            </div>
            <div className="w-[180px]">
              <StoreFilter
                value={connectionId}
                onChange={(v) => setFilter('connectionId', v)}
              />
            </div>
            <div className="w-[160px]">
              <Select
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
                options={PRODUCT_STATUSES}
                aria-label="Filter by status"
              />
            </div>
            <div className="w-[180px]">
              <Select
                value={syncStatus}
                onChange={(e) => setFilter('syncStatus', e.target.value)}
                options={SYNC_STATUSES}
                aria-label="Filter by sync state"
              />
            </div>
            <div className="w-[160px]">
              <Select
                value={sortOrder}
                onChange={(e) => setFilter('sortOrder', e.target.value)}
                options={SORT_OPTIONS}
                aria-label="Sort products"
              />
            </div>
          </div>
        </div>

        {/* Active filter summary */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 border-b border-border bg-surface-secondary/30 px-5 py-2 text-xs">
            <span className="text-text-tertiary">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}{' '}
              active
            </span>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams();
                next.set('page', '1');
                next.set('sortOrder', sortOrder);
                if (search) next.set('search', search);
                setSearchParams(next);
              }}
              className="font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Body */}
        {productsQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : productsQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm text-text-secondary">
              Failed to load products.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => productsQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : products.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-text-secondary">No products found.</p>
            <p className="mt-1 text-xs text-text-tertiary">
              {search || status || syncStatus || source || connectionId
                ? 'Try adjusting your filters.'
                : surfaceCopy.empty}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-secondary/40">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Product
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Store
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Status
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Sync
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Variants
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Published
                  </th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((p) => {
                  // Prefer the merchant's own composite preview when it
                  // exists (DESIGN_GENERATED variant-preview worker
                  // populates Product.previewImageUrl); fall back to
                  // the UPT stock thumbnail so we never render an empty
                  // square for store-published products.
                  const thumbnailUrl = p.previewImageUrl ?? p.upt.thumbnailUrl;
                  const renderInFlight =
                    p.renderSummary.pending + p.renderSummary.running > 0;
                  const renderFailed = p.renderSummary.failed > 0;
                  const isDesignGenerated = p.sourceType === 'DESIGN_GENERATED';
                  return (
                  <tr
                    key={p.id}
                    className="cursor-pointer transition-colors hover:bg-surface-secondary/60"
                    onClick={() => navigate(`/products/${p.id}`)}
                  >
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded border border-border object-cover bg-surface-secondary"
                          />
                        ) : (
                          <div className="h-10 w-10 shrink-0 rounded border border-border bg-surface-secondary" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="truncate font-medium text-text-primary">
                              {p.title}
                            </p>
                            {isDesignGenerated && (
                              <Badge
                                variant="info"
                                size="sm"
                                className="shrink-0"
                              >
                                Design
                              </Badge>
                            )}
                          </div>
                          {p.upt.name && (
                            <p className="truncate text-[11px] text-text-tertiary">
                              {p.upt.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-5 py-2.5 text-text-secondary md:table-cell max-w-[200px]">
                      <span className="block truncate">
                        {p.connection
                          ? (p.connection.externalDomain ?? p.connection.name)
                          : (
                            <span className="text-text-tertiary italic">
                              Not published
                            </span>
                          )}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <Badge
                        variant={STATUS_VARIANT[p.status] ?? 'default'}
                        size="sm"
                      >
                        {p.status.replace(/_/g, ' ')}
                      </Badge>
                      {p.requiresRepublish && (
                        <Badge variant="warning" size="sm" className="ml-1">
                          Republish
                        </Badge>
                      )}
                      {/* Render chips are only meaningful on My Products
                          (merchant-authored apparel renders). Custom Products
                          are customer-designed per order — no product-level
                          render pipeline to report. */}
                      {surface === 'my' && renderInFlight && (
                        <Badge variant="info" size="sm" className="ml-1">
                          Rendering {p.renderSummary.done}/
                          {p.renderSummary.total}
                        </Badge>
                      )}
                      {surface === 'my' && !renderInFlight && renderFailed && (
                        <Badge variant="danger" size="sm" className="ml-1">
                          Render failed
                        </Badge>
                      )}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap md:table-cell">
                      <Badge
                        variant={SYNC_VARIANT[p.syncStatus] ?? 'default'}
                        size="sm"
                      >
                        {SYNC_LABEL[p.syncStatus] ??
                          p.syncStatus.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="hidden px-5 py-2.5 text-right text-text-secondary sm:table-cell">
                      {p.variantCount}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap text-[12px] text-text-tertiary lg:table-cell">
                      {new Date(p.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td
                      className="px-5 py-2.5 text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/products/${p.id}`)}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={archiveMutation.isPending}
                          onClick={() => void handleArchiveToggle(p)}
                        >
                          {p.status === 'ARCHIVED' ? 'Restore' : 'Archive'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination != null && pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <span className="text-xs text-text-tertiary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page >= pagination.totalPages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
